import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  SignupRequest,
  EnvironmentStatusResponse,
  CreateEnvironmentResponse,
  EnvironmentFilesResponse,
  RunMetadataResponse,
  Directory,
  TaskId,
  UserTasksResponse,
  EnvironmentSettingsResponse,
  ApiError,
} from '@shared/types';

class ParticipantApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor to handle auth errors
    this.client.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', data);
    return response.data;
  }

  async signup(data: SignupRequest): Promise<{ message: string; user: any }> {
    const response = await this.client.post('/auth/signup', data);
    return response.data;
  }

  async getSignupStatus(): Promise<{ signupEnabled: boolean; signupPasscodeRequired: boolean }> {
    const response = await this.client.get<{ signupEnabled: boolean; signupPasscodeRequired: boolean }>('/auth/signup-status');
    return response.data;
  }

  // Environment Management
  async getEnvironmentStatus(): Promise<EnvironmentStatusResponse[]> {
    const response = await this.client.get<EnvironmentStatusResponse[]>('/environments/status');
    return response.data;
  }

  async startEnvironment(taskId: TaskId): Promise<CreateEnvironmentResponse> {
    const response = await this.client.post<CreateEnvironmentResponse>('/environments/start', { taskId });
    return response.data;
  }

  async stopEnvironment(environmentId: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/environments/${environmentId}/stop`);
    return response.data;
  }

  async getEnvironmentFiles(environmentId: string): Promise<Directory> {
    const response = await this.client.get<EnvironmentFilesResponse>(`/environments/${environmentId}/files`);
    return response.data.root;
  }

  async getRunMetadata(runId: string, environmentId: string): Promise<RunMetadataResponse> {
    const response = await this.client.get<RunMetadataResponse>(
      `/environments/${environmentId}/runs/${runId}/metadata`
    );
    return response.data;
  }

  async invokeEnvironment(
    prompt: string,
    environmentId: string,
    shouldRecord: boolean,
  ): Promise<{
    requestId: string;
    message: string;
    url: string;
  }> {
    const body = {
      prompt,
      shouldRecord: undefined as undefined | true,
    };
    if (shouldRecord) body.shouldRecord = true;

    const response = await this.client.post(`/environments/${environmentId}/invoke`, body);
    return response.data;
  }

  async getUserTasks(): Promise<UserTasksResponse> {
    const response = await this.client.get<UserTasksResponse>(
      `/environments/tasks`
    );
    return response.data;
  }

  async getEnvironmentSettings(): Promise<EnvironmentSettingsResponse> {
    const response = await this.client.get<EnvironmentSettingsResponse>(
      `/environments/settings`
    );
    return response.data;
  }

  async downloadCodeZip(environmentId: string): Promise<void> {
    const response = await this.client.get(`/environments/${environmentId}/download-code`, {
      responseType: 'blob',
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'code.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Environment Proxy
  getEnvironmentUrl(path: string = ''): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/api/environment${cleanPath}`;
  }

  // Error handling helper
  extractErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as ApiError | undefined;
      return apiError?.error || error.message || 'An unexpected error occurred';
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }
}

export const participantApiService = new ParticipantApiService();
export default participantApiService;
